/**
 * Generic auto-block for RECOGNIZED third-party scripts (no tagging needed).
 *
 * Many trackers (GA4, Meta Pixel, Hotjar…) load via an inline bootstrap that
 * does `document.createElement('script'); s.src = '…'; head.appendChild(s)`. We
 * override `document.createElement` so that when such a script's `src` is set to
 * a known-vendor endpoint (known-vendors.ts) whose category isn't granted yet,
 * we DON'T let it load — we stash the URL and mark the element. When the matching
 * category is later granted, we clone the element into a fresh executable script
 * (same pattern as auto-block.ts) so it finally runs.
 *
 * Scope & limits (documented for the editor/help):
 *   - Catches DYNAMICALLY-injected vendor scripts (the common case). A vendor
 *     script hard-coded as <script src> ABOVE the Consenteer snippet may already
 *     have executed during HTML parsing — place the snippet first in <head> and
 *     prefer this over relying on parse-order.
 *   - Only KNOWN vendors are touched; everything else is 100% untouched.
 *   - GTM container (gtm.js) is intentionally not blocked (it drives Consent
 *     Mode v2). See known-vendors.ts.
 *   - Complements (doesn't replace) tag-based auto-block + cookie cleanup.
 */

import { vendorCategoryForSrc } from "./known-vendors.js";
import type { ConsentChoices } from "./types.js";

const BLOCKED_ATTR = "data-csteer-blocked";
const ACTIVATED_ATTR = "data-csteer-activated";

let installed = false;
let armed = false;
/** Categories currently granted — known scripts in these load immediately. */
const granted = new Set<keyof ConsentChoices>();
/** Original createElement, kept so activated clones aren't re-intercepted. */
let nativeCreateElement: typeof document.createElement | null = null;

const protoSrcDescriptor =
  typeof HTMLScriptElement !== "undefined"
    ? Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, "src")
    : undefined;

function blockedCategory(url: string): keyof ConsentChoices | null {
  if (!armed) return null;
  const cat = vendorCategoryForSrc(url);
  if (!cat || cat === "necessary") return null;
  return granted.has(cat) ? null : cat;
}

function setRealSrc(el: HTMLScriptElement, url: string): void {
  protoSrcDescriptor?.set?.call(el, url);
}

/**
 * Shadow `src` (and route setAttribute('src')) on a freshly-created <script> so
 * we can decide, at the moment the bootstrap assigns the URL, whether to load it.
 */
function intercept(el: HTMLScriptElement): void {
  let current = "";
  Object.defineProperty(el, "src", {
    configurable: true,
    enumerable: true,
    get() {
      return current;
    },
    set(value: unknown) {
      current = typeof value === "string" ? value : String(value ?? "");
      const cat = blockedCategory(current);
      if (cat) {
        el.setAttribute("type", "text/plain");
        el.setAttribute(BLOCKED_ATTR, cat);
        el.dataset.csteerBlockedSrc = current;
        return; // do NOT trigger the native load
      }
      setRealSrc(el, current);
    },
  });

  const originalSetAttribute = el.setAttribute.bind(el);
  el.setAttribute = (name: string, value: string) => {
    if (name.toLowerCase() === "src") {
      (el as unknown as { src: string }).src = value; // route through our setter
      return;
    }
    originalSetAttribute(name, value);
  };
}

/** Install the createElement hook. Idempotent. */
export function installKnownAutoBlock(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  nativeCreateElement = document.createElement.bind(document);

  document.createElement = ((tagName: string, options?: ElementCreationOptions) => {
    const el = nativeCreateElement?.(tagName, options) as HTMLElement;
    if (typeof tagName === "string" && tagName.toLowerCase() === "script") {
      intercept(el as HTMLScriptElement);
    }
    return el;
  }) as typeof document.createElement;
}

/** Turn blocking ON. Known vendors are blocked until their category is granted. */
export function armKnownAutoBlock(): void {
  installKnownAutoBlock();
  armed = true;
}

/**
 * Clone a blocked placeholder into a fresh executable <script> (the in-place
 * placeholder won't re-run on its own) so the vendor script finally loads.
 */
function cloneAndRun(el: HTMLScriptElement): void {
  if (el.getAttribute(ACTIVATED_ATTR) === "true") return;
  const url = el.dataset.csteerBlockedSrc ?? "";
  const fresh = (nativeCreateElement ?? document.createElement.bind(document))("script");
  for (const attr of el.attributes) {
    if (attr.name === "type" || attr.name === "src") continue;
    if (attr.name === BLOCKED_ATTR || attr.name === "data-csteer-blocked-src") continue;
    fresh.setAttribute(attr.name, attr.value);
  }
  el.setAttribute(ACTIVATED_ATTR, "true");
  el.parentNode?.insertBefore(fresh, el.nextSibling);
  if (url) fresh.src = url; // fresh isn't intercepted → loads + runs
}

/** Clone every blocked script of a now-granted category. */
function activate(category: keyof ConsentChoices): void {
  if (typeof document === "undefined") return;
  for (const el of document.querySelectorAll<HTMLScriptElement>(
    `script[${BLOCKED_ATTR}="${category}"]`,
  )) {
    cloneAndRun(el);
  }
}

/** Sync granted categories from a consent choice set and activate matches. */
export function setKnownAutoBlockGrants(choices: ConsentChoices): void {
  granted.clear();
  for (const key of Object.keys(choices) as (keyof ConsentChoices)[]) {
    if (choices[key] === true) {
      granted.add(key);
      activate(key);
    }
  }
}

/**
 * Disable blocking and release anything already blocked (e.g. a stale cache
 * armed us but the feature is actually off). The createElement hook stays
 * installed but becomes a pass-through (armed = false).
 */
export function disarmAndRelease(): void {
  armed = false;
  if (typeof document === "undefined") return;
  for (const el of document.querySelectorAll<HTMLScriptElement>(`script[${BLOCKED_ATTR}]`)) {
    cloneAndRun(el);
  }
}
